import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { ImageLightbox } from "../ui/ImageLightbox";
import type { ComboDeal } from "../../types/content.types";
import { trackFacebookEvent, trackGA4Event } from "../../services/analytics";

interface ComboDealsSectionProps {
  heading: string;
  combos: ComboDeal[];
  ctaText: string;
  onCtaClick: (comboTitle: string) => void;
}

export function ComboDealsSection({
  heading,
  combos,
  ctaText,
  onCtaClick,
}: ComboDealsSectionProps) {
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);

  const handleImageClick = (combo: ComboDeal) => {
    // Track ViewContent when someone views a combo image
    trackFacebookEvent("ViewContent", {
      content_name: combo.title,
      content_category: "Combo Deal",
      content_type: "product",
    });

    trackGA4Event({
      eventName: "view_item",
      eventParams: {
        item_name: combo.title,
        item_category: "Combo Deal",
      },
    });

    setLightboxImage({ url: combo.image, alt: combo.title });
  };

  // Preload all combo deal images on component mount
  useEffect(() => {
    combos.forEach((combo) => {
      const img = new Image();
      img.src = combo.image;
    });
  }, [combos]);
  return (
    <section className="bg-black py-20 px-4">
      <div className="w-full lg:w-[92%] mx-auto px-4">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#fbbf24]">
            {heading}
          </h2>

          {/* Two Combo Deals Side-by-Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 w-full">
            {combos.map((combo, index) => (
              <div key={index} className="flex flex-col gap-4">
                {/* Combo Title */}
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-[#fbbf24] text-center min-h-[5rem] sm:min-h-[4rem] flex items-start justify-center whitespace-pre-line">
                  {combo.title}
                </h3>

                {/* Combo Image */}
                <div
                  className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-pointer transition-transform hover:scale-105"
                  onClick={() => handleImageClick(combo)}
                >
                  <img
                    src={combo.image}
                    alt={combo.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>

                {/* Price */}
                <p className="text-xl font-semibold text-gray-300 text-center">
                  {combo.price}
                </p>

                {/* Features List */}
                {combo.features && combo.features.length > 0 && (
                  <ul className="text-left text-sm sm:text-base text-gray-300 space-y-2 px-4">
                    {combo.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#8b5cf6] mt-1 flex-shrink-0">
                          ✓
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Single CTA Button Below Both Combos */}
          <div className="flex justify-center mt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => onCtaClick("Combo Deals")}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage?.url || ""}
        imageAlt={lightboxImage?.alt || ""}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        ctaText={ctaText}
        onCtaClick={() => {
          if (lightboxImage) {
            onCtaClick(lightboxImage.alt);
          }
        }}
      />
    </section>
  );
}
